import { useCallback, useEffect, useRef, useState } from "react";
import shallowEqual from "fbjs/lib/shallowEqual";

import { AbsoluteResolvedModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { FilterExpression } from "../../../Helpers/FilterExpressions/FilterExpression";
import { MultiLineFilters } from "../../../Controls/MultiControls/MultiLine/MultiLineFilter";
import { MultiLineSort } from "../../../Controls/MultiControls/MultiLine/MultiLineTypes";
import { useFormObject } from "../../../Controls/FormObjectProvider";

import { IMultilineState, IMultilineManagerClient } from "./MultilineManager";

export interface IUseMultilineOptions {
    bindingPath: AbsoluteResolvedModelPath;
    filteredUniqKey: string;
    instancesPerPage: number;
    storeFiltersWithinSession: boolean;
    useShortDebounce: boolean;
    defaultFilter: FilterExpression | undefined;
}

export interface IUseMultilineResult {
    client: IMultilineManagerClient;
    currentPage: number;
    displayedInstances: readonly string[];
    loading: boolean;
    filters: MultiLineFilters;
    sort?: MultiLineSort;
}

export function useMultiline(options: IUseMultilineOptions): IUseMultilineResult {
    const filteringManager = useFormObject().filteringManager;

    const multilineStateManager = filteringManager.getMultilineManager(options.bindingPath, options.filteredUniqKey);

    const getResultByState = useCallback(
        (state: IMultilineState): IUseMultilineResult => ({
            client: multilineStateManager,
            currentPage: state.currentPage,
            filters: state.filters,
            sort: state.sorting,
            loading: state.loading,
            displayedInstances: state.displayedInstances,
        }),
        [multilineStateManager]
    );

    const initialResult = getResultByState(multilineStateManager.getCurrentState());
    const [resultState, setResultState] = useState<IUseMultilineResult>(initialResult);
    const previousStateRef = useRef<IUseMultilineResult>();
    useEffect(
        () =>
            multilineStateManager.subscribeMultiline(
                options.instancesPerPage,
                options.storeFiltersWithinSession,
                options.useShortDebounce,
                options.defaultFilter,
                state => {
                    const nextResult = getResultByState(state);
                    const shallowResult = shallowEqual(previousStateRef.current, nextResult);
                    if (!shallowResult) {
                        setResultState(nextResult);
                    }
                    previousStateRef.current = nextResult;
                }
            ),
        [
            multilineStateManager,
            options.instancesPerPage,
            options.storeFiltersWithinSession,
            options.useShortDebounce,
            options.defaultFilter,
        ]
    );

    return resultState;
}
